<script>
import _chunk from 'lodash/chunk'
import ModalImage from '@components/modal/ModalImage'

import { setComputed } from '@state/helpers'
import formatDate, { formatLunar } from '@utils/format-date'
import formatProductType from '@utils/format-product-type'

export default {
  page() {
    return {
      title: this.order.merchant.name,
    }
  },
  components: { ModalImage },
  filters: {
    formatDate,
    formatLunar,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    tickets: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isModalImageActive: false,
      modalImageImage: '',
      windowLocation: window.location,
      adminUrl: window.globalData.APP_ADMIN_URL
    }
  },
  computed: {
    ...setComputed,
  },
  methods: {
    formatProductType,
    openModalImage(isUse, image) {
      if (!isUse) {
        this.isModalImageActive = true;
        this.modalImageImage = image
      }
    },
    chunkTicketCodes(codeArray, size) {
      return _chunk(codeArray, size)
    },
    formatText(type, data = {}) {
      switch (type) {
        case 'code':
          const takeCodeNum = 5;

          return data.code.substring(data.code.length - takeCodeNum, data.code.length);
        case 'male':
          return '男';
        case 'female':
          return '女';
        case 'unit':
          const unitMap = {
            light: '盞',
            treasury: '份',
            puja: '人',
            pudu: '份',
            worshipset: '盞',
          };

          return `共${data.qty * data.size}${unitMap[this.order.productType]}`;
        case 'light':
        case 'worshipset':
          return formatLunar(this.order.startDate);
        case 'treasury':
        case 'pudu':
          return formatDate(new Date(`${this.order.startDate}`), 'dateSlash');
        case 'puja':
          const inventoryTitle = ` ${this.order.inventoryTitle}` || '';

          return `${formatDate(this.order.startDate, 'dateTime')}${inventoryTitle}`;
        default:
          return ''
      }
    },
  },
}
</script>
<template>
  <div>
    <b-collapse
      v-for="ticket in tickets"
      :key="ticket.name"
      class="booking-section"
      :class="$style.ticket"
    >
      <div slot="trigger" slot-scope="props" class="card-header" role="button">
        <div :class="$style['ticketHeader__title']" class="card-header-title">
          <div :class="$style['ticketHeader__name']" class="has-text-primary">{{ ticket.name }}</div>
          <div
            v-if="ticket.introtext"
            class="has-text-weight-medium"
            :class="$style.ticketSubtext"
          >{{ ticket.introtext }}</div>
          <span :class="$style.orderUnittext">
            {{ formatText('unit', ticket) }}
          </span>
        </div>
        <div :class="$style['ticketHeader__icon']" class="card-header-icon">
            <span
              :class="$style.toggleText"
              v-text="props.open ? $t('global.HIDE'/* 收合 */) : $t('global.OPEN'/* 展開 */)"
            ></span>
          <span class="icon">
              <BaseIcon :name="props.open ? 'angle-up' : 'angle-down'" />
            </span>
        </div>
      </div>
      <template v-if="formatProductType('category', order.productType) === 'activity'">
        <div
          v-for="(codeGroup, groupIndex) in chunkTicketCodes(ticket.codes, ticket.size)"
          :key="groupIndex"
          :class="$style['qrcodeGroup']"
        >
          <div :class="$style['qrcodeGroup__header']">
            {{ groupIndex + 1 }} / {{ ticket.qty }}
          </div>
          <div :class="$style['qrcodeGroup__item']">
            <div
              v-for="code in codeGroup"
              :key="code.id"
              :class="[$style['qrcodeItem'],ticket.size === 1 ? $style['singleQrcode'] : '']"
            >
              <div :class="$style['qrcodeImg']">
                <img
                  alt
                  :class="code.used ? $style['isUseQrcode'] : ''"
                  :src="`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${code.id}`"
                  @click="openModalImage(code.used, `https://api.qrserver.com/v1/create-qr-code/?size=460x460&data=${code.id}`)"
                />
                <div v-if="code.used" :class="$style['iconCheck']">
                  <BaseIcon name="check" />
                </div>
              </div>
              <div
                :class="$style['qrcode-text']"
                class="has-text-weight-medium"
              >{{ $t('global.LAST_FIVE_DIGITS'/* 憑證編號末五碼 */) }}：{{formatText('code', { code: code.id })}}</div>
            </div>
          </div>
        </div>
      </template>
      <template v-if="formatProductType('category', order.productType) === 'light'">
        <b-table :data="ticket.codes" :class="$style.orderTable">
          <template slot-scope="props">
            <b-table-column label="登記資訊" :class="$style.tableColumn">
              <div>{{ props.row.fullName }}</div>
              <template v-if="order.productType === 'light'">
                <div v-if="props.row.birth">{{ props.row.birth }}</div>
              </template>
              <template v-if="order.productType === 'worshipset'">
                <div v-if="props.row.address">{{ props.row.address }}</div>
              </template>
            </b-table-column>
            <b-table-column label="序號" :class="$style.tableColumn">
              <span v-if="order.status === 0" class="has-text-grey-light">{{ $t('global.CANNOT_CHECK_CONTENT'/* 無法查看，因結緣已取消 */) }}</span>
              <span v-else-if="props.row.id || order.status === 4">{{ props.row.id }}</span>
              <span v-else class="has-text-grey-light">{{ $t('global.NOT_UPLOAD'/* 未上傳 */) }}</span>
            </b-table-column>
            <b-table-column label="圖片" :class="$style.tableColumn">
              <span v-if="order.status === 0" class="has-text-grey-light">{{ $t('global.CANNOT_CHECK_CONTENT'/* 無法查看，因結緣已取消 */) }}</span>
              <img
                v-else-if="props.row.image || order.status === 4"
                alt
                style="height: 50px; vertical-align: middle;"
                :src="`${adminUrl}/${props.row.image}`"
                @click="openModalImage(false, `${adminUrl}${props.row.image}`)"
              />
              <span v-else class="has-text-grey-light">{{ $t('global.NOT_UPLOAD'/* 未上傳 */) }}</span>
            </b-table-column>
          </template>
        </b-table>
      </template>
    </b-collapse>
    <b-modal :active.sync="isModalImageActive" :width="1024" :can-cancel="['escape','outside']">
      <ModalImage :image="modalImageImage" @close="isModalImageActive = false" />
    </b-modal>
  </div>
</template>
<style lang="scss" module>
  .ticketHeader {
    &__title {
      display: flex;
      flex-direction: column;
      align-items: baseline;
      padding: 1.5rem;
      font-weight: normal;
      .ticketSubtext {
        font-weight: 400 !important;
        line-height: 21px;
        color: #575c5c;
        text-align: justify;
        letter-spacing: 0.8px;

      }
      .orderUnittext {
        margin-top: 1rem;
        font-size: 14px;
        line-height: 18px;
        color: #852426;
        letter-spacing: 0.89px;
      }
    }
    &__name {
      @extend %typography-medium;

      font-size: 22px;
      font-weight: $heading-font-weight;
      line-height: 30px;
      letter-spacing: 1.1px;

    }
    &__icon {
      padding: 0 2rem;

      @include mobile {
        padding: 0 1.5rem;
      }
      &:hover {
        color: $color-primary;
      }
      .icon {
        margin-left: 1rem;
      }
      .toggleText {
        display: inline-block;
        width: 35px;
      }
    }
  }

  .qrcodeGroup {
    padding: 1.5rem 0;
    &:not(:last-child) {
      border-bottom: 1px dashed $color-border-base;
    }
    &__header {
      margin-bottom: 1.5rem;
      color: $grey;
    }
    &__item {
      display: flex;
      flex-wrap: wrap;
      .qrcodeItem {
        flex: 0 0 50%;
        flex-wrap: wrap;
        margin-bottom: 2rem;
        text-align: center;
        .qrcodeImg {
          position: relative;
          width: 220px;
          max-width: 35vw;
          margin: 0 auto 0.5rem;
          img:not(.isUseQrcode) {
            &:hover {
              cursor: pointer;
              box-shadow: 0 0 25px 0 rgba(178, 180, 181, 0.35);
              transition: box-shadow 0.3s;
            }
          }
        }
        .isUseQrcode {
          opacity: 0.65;
        }
        .iconCheck {
          position: absolute;
          top: 50%;
          left: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 70px;
          height: 70px;
          margin-top: -35px;
          margin-left: -35px;
          font-size: 45px;
          color: #ccff8f;
          background: #4caf4f;
          border-radius: 100%;
        }
      }
      .singleQrcode {
        flex: 0 0 100%;
      }
    }
  }

  .orderTable {
    border-bottom: 1px solid #d0d0d0;
    margin: 1.5rem 0;
    table {
      border-collapse: collapse !important;
      thead {
        tr {
          background-color: #f0e4d6;
          th {
            padding-top: 18px;
            padding-bottom: 18px;
            padding-left: 1.5rem;
            font-size: 15px;
            font-weight: 400 !important;
            line-height: 21px;
            color: #100101;
            letter-spacing: 1.03px;
            border: 1px solid #f0e4d6;
          }
        }
      }
      tbody {
        tr {
          td {
            font-size: 15px;
            font-weight: 400 !important;
            line-height: 25px;
            color: #100101;
            letter-spacing: 1.03px;
          }
        }
      }

      .tableColumn {
        padding-top: 20px;
        padding-bottom: 20px;
        padding-left: 1.5rem;
        border: 1px solid #d0d0d0;

        @include mobile {
          padding-left: 13px;
          text-align: left !important;
          align-items: center;
        }
        span {
          @include mobile {
            display: block !important;
            width: 10rem !important;
          }
        }
        img:hover {
          cursor: pointer;
          opacity: 0.75;
        }
      }
    }
  }
</style>
